import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { parseAuditEntity } from "./audit.describe";

export interface AuditEntry {
  id: string;
  actor_id: string | null;
  action: string;
  entity: string;
  before: unknown;
  after: unknown;
  created_at: string;
}

export interface AuditFilters {
  action?: string;
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
}

const DEFAULT_LIMIT = 200;

/**
 * BL-44 — the log itself.
 *
 * ⚠ READ DIRECTLY, NOT THROUGH THE EDGE FUNCTION, and the reason is the filters.
 * `GET /admin/audit` pages with a cursor and takes no column filters, so the old
 * hook fetched 100 rows and filtered them in the browser — a date range then
 * searched only the newest 100 rows and silently reported nothing for last week.
 * Superadmin holds a direct SELECT grant (0012) and `audit_superadmin_read`
 * scopes it, so the filters belong in the query, where they actually narrow the
 * whole table. Nobody else can read a row whatever they ask for.
 */
export function useAuditLog(filters: AuditFilters) {
  return useQuery({
    queryKey: ["audit-log", filters],
    queryFn: async (): Promise<AuditEntry[]> => {
      let query = supabase
        .from("audit_log")
        .select("id, actor_id, action, entity, before, after, created_at")
        .order("created_at", { ascending: false })
        .order("id", { ascending: false })
        .limit(filters.limit ?? DEFAULT_LIMIT);
      if (filters.action) query = query.eq("action", filters.action);
      if (filters.dateFrom) query = query.gte("created_at", filters.dateFrom);
      // The picker gives a date; the range has to cover that whole day.
      if (filters.dateTo) query = query.lte("created_at", `${filters.dateTo}T23:59:59.999Z`);
      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as AuditEntry[];
    },
    staleTime: 30_000,
  });
}

export function useAuditActions() {
  return useQuery<string[]>({
    queryKey: ["audit-actions"],
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await supabase.from("audit_log").select("action").order("action");
      if (error) throw error;
      return [...new Set(data?.map((row) => row.action))];
    },
    staleTime: 10 * 60_000,
  });
}

export interface AuditNames {
  /** Staff id → name, for the Who column. */
  actors: ReadonlyMap<string, { name: string; role: string }>;
  /** Entity id → a label a person recognises, for the Record column. */
  records: ReadonlyMap<string, string>;
}

const EMPTY_NAMES: AuditNames = { actors: new Map(), records: new Map() };

/**
 * Turns the uuids in a page of log rows into names.
 *
 * `audit_log` carries no foreign keys — `actor_id` is a bare uuid and `entity`
 * is the text `"table:uuid"` (0012) — so there is nothing to embed and the ids
 * are looked up per page instead. Three queries, each on a table superadmin
 * already reads, rather than one per row.
 */
export function useAuditNames(entries: readonly AuditEntry[]): AuditNames {
  const actorIds = [...new Set(entries.flatMap((entry) => entry.actor_id ?? []))].sort();
  const byTable = new Map<string, string[]>();
  for (const entry of entries) {
    const { table, id } = parseAuditEntity(entry.entity);
    if (!id) continue;
    byTable.set(table, [...(byTable.get(table) ?? []), id]);
  }
  const bookingIds = [...new Set(byTable.get("bookings") ?? byTable.get("booking") ?? [])].sort();
  const staffIds = [...new Set(byTable.get("staff") ?? [])].sort();

  const { data } = useQuery({
    queryKey: ["audit-names", actorIds, bookingIds, staffIds],
    enabled: entries.length > 0,
    queryFn: async (): Promise<AuditNames> => {
      const actors = new Map<string, { name: string; role: string }>();
      const records = new Map<string, string>();

      const people = [...new Set([...actorIds, ...staffIds])];
      if (people.length > 0) {
        const { data: rows } = await supabase
          .from("staff")
          .select("id, full_name, role")
          .in("id", people);
        for (const row of rows ?? []) {
          actors.set(row.id, { name: row.full_name, role: row.role });
          records.set(row.id, row.full_name);
        }
      }
      if (bookingIds.length > 0) {
        const { data: rows } = await supabase
          .from("bookings")
          .select("id, ref")
          .in("id", bookingIds);
        for (const row of rows ?? []) records.set(row.id, row.ref);
      }
      return { actors, records };
    },
    staleTime: 5 * 60_000,
  });

  return data ?? EMPTY_NAMES;
}
